import { useDisclosure } from '@mantine/hooks';
import { isEmpty } from 'lodash-es';
import { useMemo, useRef } from 'react';
import {
  ContextProp,
  GroupedVirtuoso,
  GroupedVirtuosoHandle,
} from 'react-virtuoso';
import * as EmptySectionContent from 'src/components/common/EmptySectionContent';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useCatalogMetricsContext } from 'src/contexts/CatalogMetricsContext';
import { Content } from 'src/contexts/ContentContext';
import { useMultiSelectionContext } from 'src/contexts/MultiSelectionContext';
import { SearchSolidIcon } from 'src/svgs/Viagogo';
import { BulkActionsDrawer } from 'src/tiles/BulkActionsContent';
import { MobileEventTilesBulkActions } from 'src/tiles/EventTile';
import { useInventoryListingBulkActions } from 'src/tiles/ListingTilesV2/ListingTilesBulkActions.hooks';
import { ContentId } from 'src/utils/constants/contentId';
import {
  ActionOutboxEntityType,
  EventWithData,
  ListingMetrics,
} from 'src/WebApiController';

import { useCollapsibleGroups } from '../InventoryEventsMobile.hooks';
import {
  EventTilesGroupHeader,
  EventTilesGroupItem,
} from './EventGroupedList.components';
import * as styles from './EventGroupedList.css';
import { PortfolioInsights } from './PortfolioInsights';

type EventTilesVirtuosoContext = {
  showInsights: boolean;
  events: EventWithData[];
  eventMetrics: Record<string, ListingMetrics> | undefined;
};

const Header = ({ context }: ContextProp<EventTilesVirtuosoContext>) => {
  return (
    context.showInsights && (
      <PortfolioInsights
        events={context.events}
        eventMetrics={context.eventMetrics}
      />
    )
  );
};

export const EventGroupedTiles = ({
  eventsTransformed,
}: {
  eventsTransformed?: EventWithData[];
}) => {
  const { eventMetrics } = useCatalogMetricsContext<ListingMetrics>();
  const { data } = useCatalogDataContext();

  const { selectionMode, itemSelection, setSelectionMode } =
    useMultiSelectionContext();
  const listingActionGroups = useInventoryListingBulkActions();
  const [showBulkActions, { toggle: toggleBulkActions }] = useDisclosure();

  const virtuosoRef = useRef<GroupedVirtuosoHandle>(null);
  const scrollRef = useRef<HTMLElement | null>(null);

  const {
    events,
    eventGroups,
    showInsights,
    updateGroupCollapsed,
    getEventFromVirtruosoGroup,
  } = useCollapsibleGroups(eventsTransformed ?? [], eventMetrics, data);

  const eventTilesVirtuosoContext = useMemo<EventTilesVirtuosoContext>(() => {
    return {
      showInsights,
      events,
      eventMetrics,
    };
  }, [eventMetrics, events, showInsights]);

  if (events.length === 0) {
    return (
      <EmptySectionContent.Root
        icon={
          <EmptySectionContent.SolidIconContainer>
            <SearchSolidIcon />
          </EmptySectionContent.SolidIconContainer>
        }
      >
        <EmptySectionContent.Label>
          <Content id={ContentId.NoResultFound} />
        </EmptySectionContent.Label>
        <EmptySectionContent.DetailMessage>
          <Content id={ContentId.NoEventsMatching} />
        </EmptySectionContent.DetailMessage>
      </EmptySectionContent.Root>
    );
  }

  return (
    <>
      <GroupedVirtuoso<EventTilesVirtuosoContext>
        id={'virtuoso-event-list-mobile'}
        scrollerRef={(el) => {
          if (el instanceof HTMLElement) scrollRef.current = el;
        }}
        components={{
          Header: Header,
        }}
        context={eventTilesVirtuosoContext}
        className={styles.virtuosoRoot}
        overscan={window.innerHeight}
        increaseViewportBy={window.innerHeight}
        groupCounts={eventGroups.map((group) =>
          group.collapsed ? 0 : group.count
        )}
        groupContent={(index) => {
          const { name, count, collapsed } = eventGroups[index];
          return (
            <EventTilesGroupHeader
              key={name}
              index={index}
              groupName={name}
              groupCount={count}
              virtuosoHandler={virtuosoRef.current}
              collapsed={collapsed}
              updateCollapse={updateGroupCollapsed(index)}
            />
          );
        }}
        itemContent={(index, groupIndex) => {
          const event = getEventFromVirtruosoGroup(index, groupIndex);
          if (event == null) {
            return null;
          }
          return (
            <EventTilesGroupItem
              event={event.event}
              metrics={eventMetrics?.[event.event.virtId]}
              selectionMode={selectionMode}
            />
          );
        }}
      />
      <MobileEventTilesBulkActions
        entityType={ActionOutboxEntityType.Listing}
        events={events ?? []}
        onBulkActionsClick={toggleBulkActions}
      />
      <BulkActionsDrawer
        entityType={ActionOutboxEntityType.Listing}
        actionGroups={listingActionGroups}
        onClose={() => {
          setSelectionMode(undefined);
          toggleBulkActions();
        }}
        show={!isEmpty(itemSelection) && showBulkActions}
      />
    </>
  );
};
